import React, { useEffect, useRef, useState } from 'react';
import apiService from '../services/ApiService';
import { BrowserMultiFormatReader } from '@zxing/browser';
import './LoginPage.css';
import FriendlyAlertModal from './FriendlyAlertModal';
import loginIcon from '../login-icon.png';

const LoginPage = ({ onLogin }) => {
  // โหมดการใช้งาน
  const [mode, setMode] = useState('employee');

  // ฟอร์มพนักงาน
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // ฟอร์มลูกค้า
  const [serial, setSerial] = useState('');
  const [serialError, setSerialError] = useState('');
  const [showSerialModal, setShowSerialModal] = useState(false);

  // สแกนบาร์โค้ด
  const [isScanning, setIsScanning] = useState(false);
  const [scanError, setScanError] = useState('');
  const videoRef = useRef(null);
  const codeReaderRef = useRef(null);

  useEffect(() => {
    setError('');
    setSerialError('');
    setScanError('');
  }, [mode]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const result = await apiService.login(formData.username, formData.password);
      if (result.success) {
        onLogin(result.user);
      } else {
        setError(result.error || 'ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง');
      }
    } catch (error) {
      setError('เกิดข้อผิดพลาดในการเข้าสู่ระบบ กรุณาลองใหม่อีกครั้ง');
    }

    setIsLoading(false);
  };

  const serialPattern = /^[A-Za-z0-9-]{4,}$/;
  const goToPublic = (value) => {
    const trimmed = (value || '').trim();
    if (!serialPattern.test(trimmed)) {
      setSerialError('รูปแบบซีเรียลไม่ถูกต้อง');
      setShowSerialModal(true);
      return;
    }
    // ใช้ absolute URL และ replace เพื่อให้เนวิเกตแน่นอนบนทุกเบราเซอร์/อุปกรณ์
    const target = `${window.location.origin}/public/serial/${encodeURIComponent(trimmed)}`;
    window.location.replace(target);
  };

  const handleSerialSubmit = (e) => {
    e.preventDefault();
    setSerialError('');
    goToPublic(serial);
  };

  const startScanner = async () => {
    setScanError('');
    try {
      const reader = new BrowserMultiFormatReader();
      codeReaderRef.current = reader;
      setIsScanning(true);
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      const result = await reader.decodeOnceFromVideoDevice(undefined, videoRef.current);
      if (result && result.text) {
        setSerial(result.text);
        stopScanner();
        goToPublic(result.text);
      }
    } catch (err) {
      console.error('Scan error:', err);
      setScanError('ไม่สามารถใช้กล้องหรือสแกนบาร์โค้ดได้');
      stopScanner();
    }
  };

  const stopScanner = () => {
    setIsScanning(false);
    try {
      const reader = codeReaderRef.current;
      if (reader) {
        reader.reset();
        codeReaderRef.current = null;
      }
      const video = videoRef.current;
      const stream = video && video.srcObject;
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
      }
      if (video) {
        video.srcObject = null;
      }
    } catch (e) {}
  };

  return (
    <div className="login-page">
      <div className="navy-gradient" />
      {/* Top rounded banner */}
      <div className="banner-container">
        <div className="login-banner" role="img" aria-label="KTPAN Battery Management">
          <img src={loginIcon} alt="KTPAN Logo" className="banner-logo" />
        </div>
      </div>
      <div className="login-container">
        <div className="brand-header">
          <i className="fas fa-battery-full brand-icon"></i>
          <div className="brand-text">
            <div className="brand-title thai-text">ระบบจัดการแบตเตอรี่</div>
            <div className="brand-sub thai-text">เลือกโหมดการใช้งาน</div>
          </div>
        </div>

        <div className="mode-switch">
          <button
            type="button"
            className={`switch-btn ${mode === 'employee' ? 'active' : ''}`}
            onClick={() => setMode('employee')}
          >
            <i className="fas fa-user-tie me-2"></i> พนักงาน
          </button>
          <button
            type="button"
            className={`switch-btn ${mode === 'customer' ? 'active' : ''}`}
            onClick={() => setMode('customer')}
          >
            <i className="fas fa-users me-2"></i> ลูกค้า
          </button>
        </div>

        {/* พนักงาน */}
        <div className={`panel ${mode === 'employee' ? 'show' : 'hide'}`}>
          <div className="card-panel">
            {error && (
              <div className="alert alert-danger mb-3">
                <i className="fas fa-exclamation-triangle me-2"></i>
                <span className="thai-text">{error}</span>
              </div>
            )}

            <form onSubmit={handleLoginSubmit}>
              <div className="mb-3">
                <label className="form-label thai-text">ชื่อผู้ใช้</label>
                <div className="input-group modern-input">
                  <span className="input-group-text"><i className="fas fa-user"></i></span>
                  <input
                    type="text"
                    className="form-control"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    placeholder="กรอกชื่อผู้ใช้"
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="form-label thai-text">รหัสผ่าน</label>
                <div className="input-group modern-input">
                  <span className="input-group-text"><i className="fas fa-lock"></i></span>
                  <input
                    type="password"
                    className="form-control"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="กรอกรหัสผ่าน"
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              <button type="submit" className="primary-btn w-100" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    <span className="thai-text">กำลังเข้าสู่ระบบ...</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-sign-in-alt me-2"></i>
                    <span className="thai-text">เข้าสู่ระบบ</span>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* ลูกค้า */}
        <div className={`panel ${mode === 'customer' ? 'show' : 'hide'}`}>
          <div className="card-panel">
            {serialError && (
              <div className="alert alert-warning mb-3">
                <i className="fas fa-info-circle me-2"></i>
                <span className="thai-text">{serialError}</span>
              </div>
            )}

            <form onSubmit={handleSerialSubmit}>
              <div className="mb-3">
                <label className="form-label thai-text">หมายเลขซีเรียล</label>
                <div className="input-group modern-input">
                  <span className="input-group-text"><i className="fas fa-hashtag"></i></span>
                  <input
                    type="text"
                    className="form-control"
                    value={serial}
                    onChange={(e) => setSerial(e.target.value)}
                    placeholder="กรอกหมายเลขซีเรียล"
                    required
                  />
                </div>
              </div>

              <div className="d-flex gap-2 mb-3">
                <button type="button" className="secondary-btn flex-fill" onClick={startScanner}>
                  <i className="fas fa-barcode me-2"></i> สแกนQRcode
                </button>
                <button type="submit" className="primary-btn flex-fill">
                  <i className="fas fa-search me-2"></i> ตรวจสอบ
                </button>
              </div>

              <p className="text-muted thai-text small">กรอกซีเรียลหรือสแกนQRcode ของสินค้าระบบจะแสดงข้อมูลการตรวจสภาพ</p>
            </form>

            {isScanning && (
              <div className="scanner-overlay">
                <div className="scanner-content">
                  <div className="scanner-header">
                    <span className="thai-text">สแกนQRcode</span>
                    <button type="button" className="icon-btn" onClick={stopScanner}>
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                  <div className="video-wrap">
                    <video ref={videoRef} className="scanner-video" muted playsInline />
                    <div className="scan-guide"></div>
                  </div>
                  {scanError && (
                    <div className="alert alert-danger mt-3">
                      <i className="fas fa-exclamation-circle me-2"></i>
                      <span className="thai-text">{scanError}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            <FriendlyAlertModal
              show={showSerialModal}
              title="ซีเรียลไม่ถูกต้อง"
              icon="🥺"
              message={(
                <div className="thai-text">
                  <p className="mb-2">กรุณาตรวจสอบหมายเลขซีเรียลที่กรอก</p>
                  <ul className="mb-2" style={{ marginLeft: '1rem' }}>
                    <li>รองรับเฉพาะตัวอักษร a-z, A-Z, ตัวเลข และเครื่องหมายขีดกลาง</li>
                    <li>ควรมีความยาวอย่างน้อย 4 ตัวอักษร</li>
                  </ul>
                  <p className="mb-0">ตัวอย่าง: <code>CHG002</code>, <code>BATT-2024-001</code></p>
                </div>
              )}
              actions={[
                { label: 'ลองใหม่', variant: 'primary', onClick: () => setShowSerialModal(false) },
              ]}
              onClose={() => setShowSerialModal(false)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;